import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Save, Eye, Loader2, X, Plus, Trash2 } from 'lucide-react';
import { adminApi, absoluteMediaUrl, formatApiErrorDetail } from './api';
import { useLanguage } from '../context/LanguageContext';
import { ADMIN_T } from './labels';
import { MediaPicker } from './MediaPicker';

const THEME_OPTIONS = [
  { value: 'sea',     ar: '🌊 البحر',      en: '🌊 Sea' },
  { value: 'sand',    ar: '🏜️ الرمال',    en: '🏜️ Sand' },
  { value: 'sky',     ar: '☁️ السماء',     en: '☁️ Sky' },
  { value: 'trees',   ar: '🌳 الأشجار',    en: '🌳 Trees' },
  { value: 'unified', ar: '🌍 بيئات التوازن', en: '🌍 Unified Nature' },
];
const CATEGORY_OPTIONS = [
  { value: 'marine', ar: 'بحرية', en: 'Marine' },
  { value: 'desert', ar: 'الصحراء', en: 'Desert' },
  { value: 'forest', ar: 'الغابات والحياة الفطرية', en: 'Forest & Wildlife' },
  { value: 'air',    ar: 'الهواء والمناخ', en: 'Air & Climate' },
  { value: 'all',    ar: 'متعدد المواضيع', en: 'Cross-topic' },
];

const blankArticle = {
  title_ar: '', title_en: '',
  excerpt_ar: '', excerpt_en: '',
  content_ar: [], content_en: [],
  image: '', gallery: [],
  category: 'all', theme: 'unified',
  tag_ar: '', tag_en: '',
  author_ar: '', author_en: '',
  pull_quote_ar: '', pull_quote_en: '',
  read_minutes: 5,
  status: 'draft',
  publish_date: new Date().toISOString().slice(0, 10),
};

const AdminNewsEditor = () => {
  const { id } = useParams();
  const isNew = id === 'new';
  const nav = useNavigate();
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = ADMIN_T[language] || ADMIN_T.en;

  const [form, setForm] = useState(blankArticle);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [picker, setPicker] = useState(null); // { target: 'image' | 'gallery' }

  useEffect(() => {
    if (isNew) return;
    setLoading(true);
    adminApi.get(`/news/${id}`)
      .then(({ data }) => setForm({ ...blankArticle, ...data }))
      .catch((e) => setError(formatApiErrorDetail(e?.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = useCallback(async (asStatus) => {
    setSaving(true);
    setError('');
    const payload = { ...form };
    if (asStatus) payload.status = asStatus;
    // normalize content arrays (paragraph per line)
    if (typeof payload.content_ar === 'string')
      payload.content_ar = payload.content_ar.split('\n').map(s => s.trim()).filter(Boolean);
    if (typeof payload.content_en === 'string')
      payload.content_en = payload.content_en.split('\n').map(s => s.trim()).filter(Boolean);
    try {
      const { data } = isNew
        ? await adminApi.post('/news', payload)
        : await adminApi.put(`/news/${id}`, payload);
      if (isNew) nav(`/admin/news/${data.id}`, { replace: true });
      else setForm({ ...blankArticle, ...data });
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  }, [form, id, isNew, nav]);

  // Content arrays as multi-line strings for the UI
  const contentArText = Array.isArray(form.content_ar) ? form.content_ar.join('\n') : form.content_ar;
  const contentEnText = Array.isArray(form.content_en) ? form.content_en.join('\n') : form.content_en;

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-brand-blue" /></div>;
  }

  const txt = isAr ? 'text-right' : 'text-left';
  const ArrowLead = isAr ? null : ArrowLeft; // simple back icon

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* Top bar */}
      <div className={`flex flex-wrap items-center gap-3 ${isAr ? 'flex-row-reverse' : ''}`}>
        <Link
          to="/admin/news"
          data-testid="admin-news-back"
          className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-sm font-semibold ${isAr ? 'flex-row-reverse' : ''}`}
        >
          {ArrowLead && <ArrowLead className="w-4 h-4" />}
          {isAr && <span style={{ transform: 'scaleX(-1)', display: 'inline-block' }}><ArrowLeft className="w-4 h-4" /></span>}
          {t.back}
        </Link>
        <h1 className="text-xl md:text-2xl font-bold flex-1 text-slate-900 dark:text-white">
          {isNew ? t.addArticle : (isAr ? (form.title_ar || '—') : (form.title_en || '—'))}
        </h1>

        {form.id && form.status === 'published' && (
          <a
            href={`/news/${form.id}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="admin-news-preview"
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-sm font-semibold ${isAr ? 'flex-row-reverse' : ''}`}
          >
            <Eye className="w-4 h-4" /> {t.preview}
          </a>
        )}
        <button
          type="button"
          onClick={() => save('draft')}
          disabled={saving}
          data-testid="admin-news-save-draft"
          className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-200 text-sm font-semibold disabled:opacity-60 ${isAr ? 'flex-row-reverse' : ''}`}
        >
          {saving && <Loader2 className="w-4 h-4 animate-spin" />} {t.saveDraft}
        </button>
        <button
          type="button"
          onClick={() => save('published')}
          disabled={saving}
          data-testid="admin-news-publish"
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-brand-blue to-brand-blue-700 text-white text-sm font-semibold shadow-md hover:shadow-lg disabled:opacity-60 ${isAr ? 'flex-row-reverse' : ''}`}
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {isNew ? t.publish : t.update}
        </button>
      </div>

      {error && (
        <div className={`rounded-xl px-4 py-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-400/30 text-sm text-red-700 dark:text-red-300 ${txt}`}>{error}</div>
      )}

      {/* Featured image */}
      <Section title={t.featuredImage} isAr={isAr}>
        {form.image ? (
          <div className="relative">
            <img src={absoluteMediaUrl(form.image)} alt="" className="w-full max-h-80 object-cover rounded-xl border border-slate-200 dark:border-slate-700" />
            <button type="button" onClick={() => update('image', '')} aria-label="Remove" className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center">
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setPicker({ target: 'image' })}
            className="w-full h-44 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-700 text-sm text-slate-500 hover:border-brand-blue hover:bg-brand-blue/5 transition"
          >
            {t.uploadOrPaste}
          </button>
        )}
      </Section>

      {/* Titles */}
      <div className="grid md:grid-cols-2 gap-4">
        <Field label={t.titleAr} dir="rtl">
          <input className={inputCls} dir="rtl" value={form.title_ar} onChange={(e) => update('title_ar', e.target.value)} data-testid="news-title-ar" />
        </Field>
        <Field label={t.titleEn} dir="ltr">
          <input className={inputCls} dir="ltr" value={form.title_en} onChange={(e) => update('title_en', e.target.value)} data-testid="news-title-en" />
        </Field>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Field label={t.excerptAr} dir="rtl">
          <textarea rows={3} className={inputCls} dir="rtl" value={form.excerpt_ar} onChange={(e) => update('excerpt_ar', e.target.value)} />
        </Field>
        <Field label={t.excerptEn} dir="ltr">
          <textarea rows={3} className={inputCls} dir="ltr" value={form.excerpt_en} onChange={(e) => update('excerpt_en', e.target.value)} />
        </Field>
      </div>

      {/* Full content */}
      <div className="grid md:grid-cols-2 gap-4">
        <Field label={`${t.contentAr} (${t.paragraphHint})`} dir="rtl">
          <textarea
            rows={10} className={inputCls + ' leading-loose'} dir="rtl"
            value={contentArText}
            onChange={(e) => update('content_ar', e.target.value.split('\n'))}
            data-testid="news-content-ar"
          />
        </Field>
        <Field label={`${t.contentEn} (${t.paragraphHint})`} dir="ltr">
          <textarea
            rows={10} className={inputCls + ' leading-loose'} dir="ltr"
            value={contentEnText}
            onChange={(e) => update('content_en', e.target.value.split('\n'))}
            data-testid="news-content-en"
          />
        </Field>
      </div>

      {/* Meta */}
      <div className="grid md:grid-cols-3 gap-4">
        <Field label={t.theme}>
          <select value={form.theme} onChange={(e) => update('theme', e.target.value)} className={inputCls} data-testid="news-theme">
            {THEME_OPTIONS.map((o) => <option key={o.value} value={o.value}>{isAr ? o.ar : o.en}</option>)}
          </select>
        </Field>
        <Field label={t.category}>
          <select value={form.category} onChange={(e) => update('category', e.target.value)} className={inputCls}>
            {CATEGORY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{isAr ? o.ar : o.en}</option>)}
          </select>
        </Field>
        <Field label={t.publishDate}>
          <input type="date" className={inputCls} value={form.publish_date || ''} onChange={(e) => update('publish_date', e.target.value)} />
        </Field>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <Field label={t.tagAr} dir="rtl">
          <input className={inputCls} dir="rtl" value={form.tag_ar} onChange={(e) => update('tag_ar', e.target.value)} />
        </Field>
        <Field label={t.tagEn} dir="ltr">
          <input className={inputCls} dir="ltr" value={form.tag_en} onChange={(e) => update('tag_en', e.target.value)} />
        </Field>
        <Field label={t.readMinutes}>
          <input type="number" min={1} max={120} className={inputCls} value={form.read_minutes} onChange={(e) => update('read_minutes', Number(e.target.value) || 1)} />
        </Field>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Field label={t.authorAr} dir="rtl">
          <input className={inputCls} dir="rtl" value={form.author_ar} onChange={(e) => update('author_ar', e.target.value)} />
        </Field>
        <Field label={t.authorEn} dir="ltr">
          <input className={inputCls} dir="ltr" value={form.author_en} onChange={(e) => update('author_en', e.target.value)} />
        </Field>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Field label={t.pullQuoteAr} dir="rtl">
          <textarea rows={2} className={inputCls} dir="rtl" value={form.pull_quote_ar} onChange={(e) => update('pull_quote_ar', e.target.value)} />
        </Field>
        <Field label={t.pullQuoteEn} dir="ltr">
          <textarea rows={2} className={inputCls} dir="ltr" value={form.pull_quote_en} onChange={(e) => update('pull_quote_en', e.target.value)} />
        </Field>
      </div>

      {/* Gallery */}
      <Section title={t.gallery} isAr={isAr}>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {(form.gallery || []).map((g, i) => (
            <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700">
              <img src={absoluteMediaUrl(g)} alt="" className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => update('gallery', form.gallery.filter((_, idx) => idx !== i))}
                className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center"
                aria-label="Remove"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => setPicker({ target: 'gallery' })}
            className="aspect-square rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-700 hover:border-brand-blue hover:bg-brand-blue/5 transition flex flex-col items-center justify-center text-slate-500 hover:text-brand-blue"
          >
            <Plus className="w-6 h-6 mb-1" />
            <span className="text-xs">{t.uploadOrPaste}</span>
          </button>
        </div>
      </Section>

      <MediaPicker
        open={!!picker}
        onClose={() => setPicker(null)}
        onSelect={(url) => {
          if (!picker) return;
          if (picker.target === 'image') update('image', url);
          else update('gallery', [...(form.gallery || []), url]);
        }}
      />
    </div>
  );
};

const inputCls =
  'w-full px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-blue/40';

const Field = ({ label, dir, children }) => (
  <div>
    <label dir={dir} className={`block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1.5 ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>
      {label}
    </label>
    {children}
  </div>
);

const Section = ({ title, isAr, children }) => (
  <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 md:p-5">
    <h3 className={`text-sm font-bold text-slate-900 dark:text-white mb-3 ${isAr ? 'text-right' : 'text-left'}`}>{title}</h3>
    {children}
  </div>
);

export default AdminNewsEditor;
